import { useEffect, useState } from "react";
import { FaPlayCircle, FaMusic, FaPlus } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { useCombinedAuthHook } from "../../hooks/combined-auth.hook";
import { getForUser } from "../../hooks/database-file";
import { useResetScroll } from "../../hooks/reset-scroll.hook";
import { useTitle } from "../../hooks/useTitle.hook";
import { authState } from "../../state/auth.state";
import type { UserTracks } from "../../types";
import { encodeString } from "../../utils/crypto-utils";
import Button from "../../components/button/button.component";

const DashboardPage = () => {
  useTitle("My Library");
  const authUser = useRecoilValue(authState);
  const [stateTracks, setTracks] = useState([] as UserTracks[]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  useResetScroll();
  useCombinedAuthHook();

  const playerColor = [
    "bg-indigo-600",
    "bg-purple-600",
    "bg-blue-600",
    "bg-cyan-600",
    "bg-teal-600",
    "bg-emerald-600",
    "bg-violet-600",
    "bg-fuchsia-600",
    "bg-pink-600",
    "bg-rose-600",
  ];

  useEffect(() => {
    if (!authUser) return;

    setIsLoading(true);
    getForUser(authUser.uid)
      .then((tracks: UserTracks[] | null) => {
        if (tracks) {
          setTracks(tracks);
        }
      })
      .finally(() => {
        setIsLoading(false);
      })
  }, [authUser]);

  const toReadableDate = (date?: string) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const generatePlayerBg = (track: string, index: number) => {
    return !track ? playerColor[index % playerColor.length] : ``;
  };

  const handleTrackClick = (track: UserTracks) => {
    navigate(`/app/track/${encodeString(authUser.uid)}/${track.fileId}`);
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      <header className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-center">My Library</h1>
      </header>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center min-h-[200px]">
          <div className="w-12 h-12 rounded-full border-4 border-gray-500 border-t-transparent animate-spin"></div>
          <p className="mt-4 text-gray-600">Loading your tracks...</p>
        </div>
      ) : (
        <>
          {!stateTracks || stateTracks.length === 0 ? (
            <EmptyLibrary />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4 lg:gap-5">
              {[...stateTracks].reverse().map((track, idx) => (
                <TrackCard
                  key={idx}
                  track={track}
                  index={idx}
                  generatePlayerBg={generatePlayerBg}
                  toReadableDate={toReadableDate}
                  onClick={() => handleTrackClick(track)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const TrackCard = ({
  track,
  index,
  generatePlayerBg,
  toReadableDate,
  onClick,
}: {
  track: UserTracks;
  index: number;
  generatePlayerBg: (track: string, index: number) => string;
  toReadableDate: (date?: string) => string;
  onClick: () => void;
}) => {
  return (
    <div
      className="group flex flex-col rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 active:scale-95 h-full cursor-pointer bg-white"
      onClick={onClick}
    >
      <div className="relative aspect-square overflow-hidden">
        {track.coverPage ? (
          <img
            src={`data:image/png;base64,${track.coverPage}`}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            alt={track.trackUid || "Track cover"}
          />
        ) : (
          <div className={`w-full h-full flex items-center justify-center ${generatePlayerBg(track.coverPage, index)}`}>
            <FaMusic className="text-white/80 text-4xl" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
          <div className="p-3 w-full">
            <div className="flex items-center gap-2 text-white">
              <FaPlayCircle className="text-xl" />
              <span className="font-medium">Play</span>
            </div>
          </div>
        </div>
      </div>
      <div className="p-3 flex flex-col flex-grow">
        <h3 className="font-semibold text-sm sm:text-base line-clamp-1 text-gray-800">
          {track.trackUid || "Untitled Track"}
        </h3>
        <p className="text-xs text-gray-500 mt-1">{toReadableDate(track.date)}</p>
      </div>
    </div>
  )
};

const EmptyLibrary = () => {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center rounded-xl bg-gray-50 border border-gray-100">
      <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center mb-4">
        <FaMusic className="text-gray-500 text-xl" />
      </div>
      <h2 className="text-xl font-semibold text-gray-800 mb-2">Your library is empty</h2>
      <p className="text-gray-600 max-w-md mb-6">
        Start by uploading your first PDF to begin boosting your productivity.
      </p>
      <Button
        className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
        click={() => (window.location.href = "/app/upload")}
        type="contrast"
      >
        <FaPlus className="text-sm" />
        <span>Upload PDF</span>
      </Button>
    </div>
  );
};

export default DashboardPage;
